<template>
    <b-popover
        :target="id"
        triggers="click focus"
        placement="vertical"
        v-if="event.customer"
        custom-class="popover-animation-on-enter"
        boundary="window"
        @shown="onShown"
        @hide="onHidden"
        :no-fade="$screen.width <= 576"
        id="calendar-popover-main"
    >
        <template slot="title">
            <div class="row align-items-center">
                <div class="col">
                    <div v-if="showDate" class="mb-2 font-weight-bold">{{ event.startAt | moment("ll") }}</div>
                    {{ event.startAt | moment("LT") }} -
                    {{ event.endAt | moment("LT") }}
                </div>
                <div class="col-auto p-1" v-if="!event.isBlocker">
                    <status-badge class="pointer" :status="event.status" @click="toggleEventStatus"
                                  :iconOnly="false"></status-badge>
                </div>
            </div>
        </template>
        <div
            class="row mt-3 pointer"
            @click="$router.push({ name: 'people.customer', params: { id: event.customerId } }); removeBackdrop();"
            v-if="!event.isBlocker"
        >
            <div class="col-auto pr-0" v-if="event.customer.avatar">
                <avatar
                    :src="event.customer.avatar.preview"
                    :color="event.customer.color"
                    :userName="{ first_name: event.customer.first_name, last_name: event.customer.last_name }"
                    type="circle"
                    size="avatar-sm"
                    textSize="h2 mb-n1"
                ></avatar>
            </div>
            <div class="col">
                <h4 class="text-primary mb-1">{{ event.customer.first_name }} {{ event.customer.last_name }}</h4>
                <p class="mb-0 small">{{ event.customer.phone }}</p>
                <p class="small mb-1" v-if="event.customer.email">{{ event.customer.email }}</p>
                <tag-pills :tags="event.customer.tags"/>
            </div>
            <div class="col-auto" v-if="$loadModule('billing')">
                <div class="small mb-n1">{{ $t('Balance') }}:</div>
                <money
                    :class="{'text-danger':event.customer.balance<0, 'text-success':event.customer.balance>0}"
                    :value="event.customer.balance"
                />
            </div>
        </div>

        <hr class/>
        <div class="popover-content container-fluid">
            <div class="row" v-for="(service, serviceIndex ) in event.services" :key="serviceIndex">
                <div class="col">
                    <div v-if="service.user" class="mb-3">
                        <h4 class="text-dark">{{ service.user.firstName }} {{ service.user.lastName }}</h4>
                        <span v-if="service.treatment">
                            {{ service.duration | duration }}
                            {{ service.treatment.label }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="event.notes">
            <div class="col-1">
                <i class="fe fe-message-circle"></i>
            </div>
            <div class="col">{{ event.notes }}</div>
        </div>
        <div class="row mt-5 mt-md-4">
            <template v-if="!completed">
                <div class="col">
                    <button
                        class="btn btn-white mr-2"
                        v-b-tooltip
                        :title="$t('Edit/Reschedule appointment')"
                        @click="editAppointment"
                    >
                        <i class="fe fe-edit"></i>
                    </button>
                    <confirmable-button
                        style="display:inline-block"
                        class="mr-2"
                        v-b-tooltip
                        :title="$t('Cancel appointment')"
                        v-if="!past || event.isBlocker"
                        @confirm="cancelAppointment"
                    >
                        <i class="fe fe-x-circle text-danger"></i>
                    </confirmable-button>
                    <confirmable-button
                        style="display:inline-block"
                        v-b-tooltip
                        :title="$t('No show')"
                        class="mr-2"
                        v-else
                        @confirm="noShow"
                    >
                        <i class="fe fe-eye-off text-danger"></i>
                    </confirmable-button>
                </div>
                <div class="col-auto d-flex">
                    <button class="btn btn-light d-sm-none d-block mr-2" @click.prevent="close">
                        {{$t('Close')}}
                    </button>
                    <button
                        v-b-tooltip
                        :title="$t('Confirm')"
                        class="btn btn-success"
                        @click="confirmDraft()"
                        v-if="event.draft"
                    >
                        <i class="fe fe-check"></i>
                    </button>

                    <button
                        class="btn btn-secondary px-4 d-flex justify-content-center align-items-center"
                        @click="close();openCheckoutModal(event)"
                        v-else-if="!event.isBlocker && $loadModule('billing')"
                    >
                        <i class="fe fe-dollar-sign mr-2"></i>
                        <span class="small">{{ $t('Checkout') }}</span>
                    </button>
                </div>
            </template>
            <div class="col d-flex justify-content-end align-items-end" v-else>
                <button class="btn btn-light d-sm-none d-block mr-2" @click.prevent="close">
                    {{$t('Close')}}
                </button>
                <button
                    type="button"
                    @click="close(); invoiceModal(event.invoiceId, false)"
                    class="btn btn-white"
                >
                    {{ $t('Invoice') }}
                </button>
            </div>
        </div>
    </b-popover>
</template>

<script>
import Avatar from '@/components/Avatar.vue';
import StatusBadge from '@/components/StatusBadge';
import ConfirmableButton from '@/components/ConfirmableButton';
import Money from '@/components/Money';
import TagPills from '@/components/TagPills.vue';
import duration from '@/filters/duration.js';
import {modalMixins} from '@/mixins/modal.js';
import {mapActions} from 'vuex';

export default {
    props: {
        'id': {
            type: String,
            required: true,
        }, 
        'event': {
            type: Object,
            required: true,
        },
        "showDate": {
            type: Boolean,
            default: false,
        },
    },
    filters: {duration},
    mixins: [modalMixins],
    components: {
        Money,
        Avatar,
        TagPills,
        StatusBadge,
        ConfirmableButton
    },
    data() {
        return {
            isPopoverClosed: false
        }
    },
    computed: {
        completed() {
            return this.event.status === 'completed';
        },

        past() {
            return this.$moment(this.event.startAt).local() < this.$moment().local()
        },
    },
    methods: {
        ...mapActions('appointment', ['patch']),
        ...mapActions('customers', {patchCustomer: 'patch'}),

        editAppointment() {
            this.close();
            let appointment = {
                model: this.event,
                _id: 'temp'
            };
            const {Appointment} = this.$FeathersVuex
            new Appointment(appointment);

            let id = (this.event.isBlocker) ? "BlockAppointmentTimeModal" : 'AppointmentFastModal';

            this.openModal({id: id, model: this.event});
        },
        // openCheckoutModal() {
        //     if (this.completed) return;
        //     let details = {
        //         appointment: this.event,
        //         _id: 'temp'
        //     };
        //     const { Appointment } = this.$FeathersVuex;
        //     new Appointment(details);
        //     this.openModal({ id: 'CheckoutModal' });
        //     this.$root.$emit('bv::hide::popover');

        // },
        toggleEventStatus() {
            if (this.completed)
                return;
            let status = 'confirmed';
            if (this.event.status === 'confirmed')
                status = 'notConfirmed';

            this.patch([this.event._id, {status}]);
        },
        confirmDraft() {
            if (!this.event.draft)
                return;

            this.patch([this.event._id, { status: 'confirmed', draft: false }]);
            this.patchCustomer([this.event.customerId, { draft: false }]);
        },
        close() {
            this.$root.$emit('bv::hide::popover');
        },
        setStatus(status) {
            this.removeBackdrop()
            this.patch([this.event._id, {status: status}]);
        },
        cancelAppointment() {
            this.setStatus('cancelled');
        },
        noShow() {
            this.setStatus('noshow');
        },
        onShown() {
            if (this.$screen.width >= 576) return
            let newNode = document.createElement('div');
            const root = document.getElementsByTagName( 'html' )[0];
            newNode.className = 'modal-backdrop modal-backdrop-enter';
            newNode.setAttribute("id", "popover-modal-backdrop");
            document.body.appendChild(newNode);
            document.body.style.overflow = 'hidden';
            root.style.overflow = 'hidden';
        },
        onHidden() {
            this.removeBackdrop()
        },
        removeBackdrop() {
            if (this.$screen.width >= 576) return
            const root = document.getElementsByTagName( 'html' )[0];
            const popover = document.getElementById("calendar-popover-main");
            popover.classList.add('popover-animation-on-leave');
            const backdrop = document.getElementById("popover-modal-backdrop");
            backdrop.classList.add('modal-backdrop-leave')
            setTimeout( () => {
                if (this.$screen.width >= 576) return
                document.getElementById("popover-modal-backdrop").remove();
                document.body.style.overflow = '';
                root.style.overflow = '';
            }, 200)
        }
    },
}
</script>
<style scoped lang="scss">
@media only screen and (max-width: 576px) {
    .popover {
        top: inherit !important;
        width: 100vw;
        max-width: 100vw !important;
        min-width: 100vw !important;
        transform: inherit !important;
        border-radius: 15px 15px 0 0;
        position: fixed !important;
        padding: 25px 20px 30px 20px;
        min-height: 400px!important;
    }
}

.popover {
    max-width: 450px;
    width: 85%;
}
</style>

<style lang="scss">
@media only screen and (max-width: 576px) {
    .popover {
        & .arrow {
            display: none !important;
        }
        .popover-content {
            max-height: 350px!important;
            overflow-y: auto;
            min-height: 220px;
        }
    }

    .popover.popover-animation-on-enter {
        animation: from-bottom-enter .1s ease-out forwards;
    }
    @keyframes from-bottom-enter {
        0% { bottom: -500px; } 100% { bottom: -10px; }
    }

    .popover.popover-animation-on-leave {
        animation: from-bottom-leave .2s ease-in forwards;
    }
    @keyframes from-bottom-leave {
        0% { bottom: -10px; } 100% { bottom: -500px; }
    }

    .modal-backdrop-enter {
        animation: fade-in .2s ease-in-out forwards;
    }
    @keyframes fade-in {
        0% { opacity: 0 } 100% { opacity: .5 }
    }

    .modal-backdrop-leave {
        animation: fade-out .2s ease-in-out forwards;
    }
    @keyframes fade-out {
        0% { opacity: .5 } 100% { opacity: 0 }
    }
}
</style>
